import { useState, useCallback } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { useCreateKeywordGroup, useAllKeywordGroups } from "app/services/keyword-groups/hooks";
import { KeywordGroupEditorHelpers } from "app/pages/keyword-analysis/KeywordGroupEditorHelpers";
import { allTrackers } from "app/services/track/track";
import { keywordGroupsQueryClient } from "app/services/keyword-groups";
import DurationService from "app/services/DurationService";
import { proNavigator } from "single-spa/react-app/ProNavigator";

interface IUseKeywordGroupCreationProps {
    keywords: string[];
    defaultGroupName?: string;
    trackingContext?: string;
    onSuccess?: (groupId: string, groupName: string) => void;
    onCancel?: () => void;
    targetState?: string;
    shouldShowSuccessToast?: boolean;
}

interface IUseKeywordGroupCreationReturn {
    isOpen: boolean;
    isLoading: boolean;
    errorMessage: string | null;
    openModal: () => void;
    closeModal: () => void;
    handleCreateGroup: (name: string) => Promise<void>;
    handleViewGroup: () => void;
    handleStartOver: () => void;
}

export const useKeywordGroupCreation = ({
    keywords,
    defaultGroupName = "",
    trackingContext = "keyword_group_creation",
    onSuccess,
    onCancel,
    targetState,
    shouldShowSuccessToast = true,
}: IUseKeywordGroupCreationProps): IUseKeywordGroupCreationReturn => {
    const { params } = useCurrentStateAndParams();
    const dispatch = useDispatch();

    const { mutateAsync: createKeywordGroup } = useCreateKeywordGroup();
    const { userGroups: keywordGroups, refetch: refetchKeywordGroups } = useAllKeywordGroups();
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [groupId, setGroupId] = useState("");

    const maxKeywordsInGroup = KeywordGroupEditorHelpers.getMaxGroupCount() as number;
    const isRowLimitExceeded = keywords.length > maxKeywordsInGroup;

    const openModal = useCallback(() => {
        setIsOpen(true);
        setErrorMessage(null);
        allTrackers.dispatchTrackEvent("Button", "click", `Save to List/${trackingContext}`);
    }, [trackingContext]);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setErrorMessage(null);
        onCancel?.();
    }, [onCancel]);

    const handleCreateGroup = useCallback(
        async (name: string) => {
            const validation = await KeywordGroupEditorHelpers.validateTitle(name);

            if (!validation.isValid) {
                setErrorMessage(validation.errorMessage);
                return;
            }

            setIsLoading(true);
            setErrorMessage(null);

            try {
                const selectedKeywordsToSubmit = isRowLimitExceeded ? keywords.slice(0, maxKeywordsInGroup) : keywords;

                const group = {
                    Name: name,
                    Keywords: selectedKeywordsToSubmit.map((keyword) => keyword.toLowerCase()),
                    GroupHash: null,
                    Id: null,
                };

                const createdGroup = await createKeywordGroup(group);

                if (createdGroup) {
                    await keywordGroupsQueryClient.invalidateAll();
                    await refetchKeywordGroups();

                    if (createdGroup.Id) {
                        setGroupId(createdGroup.Id);
                    }

                    setIsOpen(false);
                    allTrackers.dispatchTrackEvent(
                        "Button",
                        "click",
                        `Create New List Clicked/${trackingContext}/${selectedKeywordsToSubmit.length}`,
                    );

                    if (shouldShowSuccessToast) {
                        dispatch(showSuccessToast(translate("create.list.wizard.toast", { listName: name })));
                    }

                    onSuccess?.(createdGroup?.Id || "", name);
                }
            } catch (error) {
                const customErrorMessage = "Error creating keyword group";
                Sentry.error(customErrorMessage, error);
                setErrorMessage(customErrorMessage);
            } finally {
                setIsLoading(false);
            }
        },
        [
            keywords,
            isRowLimitExceeded,
            maxKeywordsInGroup,
            createKeywordGroup,
            refetchKeywordGroups,
            trackingContext,
            shouldShowSuccessToast,
            onSuccess,
        ],
    );

    const handleViewGroup = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", `Analyze Clicked/${trackingContext}`);

        const targetStateToUse = targetState || "organicsearch_keywordAnalysis_overview_2";
        const { from, to } = DurationService.getDurationData(params.duration).forAPI;

        proNavigator.go(targetStateToUse, {
            ...params,
            from,
            to,
            country: params.country,
            keyword: `*${groupId}`,
            isTerm: false,
        });
    }, [targetState, params.country, params.duration, groupId, trackingContext]);

    const handleStartOver = useCallback(() => {
        allTrackers.dispatchTrackEvent("Button", "click", `Success Popup Closed/${trackingContext}`);
        setGroupId("");
    }, [trackingContext]);

    return {
        isOpen,
        isLoading,
        errorMessage,
        openModal,
        closeModal,
        handleCreateGroup,
        handleViewGroup,
        handleStartOver,
    };
};
